<template>
  <v-card class="points-config-card">
    <v-card-title>
      <span class="points-title">Pontos</span>
    </v-card-title>
    <v-card-text>
      <div>
        <mf-toggle v-model="points.homeButton" label="Exibir botão de pontos na home" />
        <hr class="divider" />

        <div class="main-content">
          <div class="definitions-section">
            <span class="title">Definições</span>
            <div class="date-fields">
              <div class="date-field">
                <span class="definitions-subtitle">Data inicial</span>
                <date-picker
                  :min="DateTime.now().toISODate()"
                  :date.sync="points.start_date"
                  type="date"
                  dense
                  outlined
                  :error="!!startDateError"
                  :error-messages="startDateError"
                />
              </div>
              <div class="date-separator" />
              <div class="date-field">
                <span class="definitions-subtitle">Data final (opcional)</span>
                <date-picker
                  :min="DateTime.now().toISODate()"
                  :date.sync="points.end_date"
                  type="date"
                  dense
                  outlined
                  :error="!!endDateError"
                  :error-messages="endDateError"
                />
              </div>
            </div>
            <div class="rate-field">
              <span class="definitions-subtitle">Taxa de pontuação</span>
              <v-text-field
                v-model="points.rate"
                type="number"
                outlined
                dense
                class="rate-input"
                suffix="pontos/R$"
                :error="!!rateError"
                :error-messages="rateError"
              />
            </div>
          </div>

          <div class="expiration-section">
            <div class="expiration-header">
              <span class="title">Expiração dos pontos</span>
              <mf-tabs-button v-model="points.durationType" :buttons="tabs" />
            </div>

            <div class="expiration-content">
              <template v-if="points.durationType === 'DYNAMIC_DATE'">
                <p class="expirations-subtitle dynamic-description">
                  Com essa opção selecionada, os pontos de cada consumidor expiram em uma data variável. Selecione após quantos dias os pontos irão expirar.
                </p>
                <div class="dynamic-input-container">
                  <div class="amount-input">
                    <mf-number-input
                      v-model="points.duration.amount"
                      :min="1"
                      :max="1000000"
                      outlined
                      dense
                      label="Expiração em dias"
                      :error="!!dynamicAmountError"
                      :error-messages="dynamicAmountError"
                    />
                  </div>
                  <p class="expirations-subtitle dynamic-result">
                    Os pontos expiram em
                    <strong class="highlight-text"> {{ points.duration.amount }} {{ [0, 1].includes(points.duration.amount) ? 'dia' : 'dias' }} </strong>
                  </p>
                </div>
              </template>
              <template v-else>
                <div class="fixed-date-container">
                  <p class="expirations-subtitle fixed-description">
                    Os novos pontos adquiridos irão expirar no dia
                    <strong class="highlight-text">{{ formattedFixedDate || 'xx/xx/xxxx' }}</strong
                    >.
                  </p>
                  <div class="fixed-date-picker">
                    <date-picker
                      :min="DateTime.now().toISODate()"
                      :date="points.duration.fixed_date"
                      type="date"
                      dense
                      outlined
                      label="Data de expiração"
                      :error="!!fixedDateError"
                      :error-messages="fixedDateError"
                      @input="points.duration.fixed_date = $event"
                      @blur="fixedDateTouched = true"
                    />
                  </div>
                </div>
              </template>
            </div>
          </div>

          <div class="pdf-section">
            <span class="title">Regulamento em PDF (opcional)</span>
            <input ref="pdfInput" class="hidden-input" type="file" accept="application/pdf" @input="readPdf" />
            <mf-button
              :label="hasPdf ? 'Alterar PDF' : 'Selecionar PDF'"
              :loading="pdfFile.isLoading"
              :disabled="saving || pdfFile.isLoading"
              @click="triggerPdfInput"
            />
            <span v-if="points.ruleUrl" class="file-chip">
              <mf-icon fill medium color="#2B67E5">insert_drive_file</mf-icon>
              <span class="file-name"> {{ pdfFileName }}</span>
              <mf-icon class="close-icon" icon="close" @click="deletePdf" />
            </span>

            <p class="pdf-hint">O formato aceito é: <span class="highlight-text">PDF</span>.</p>
            <p class="pdf-hint">O tamanho máximo permitido é de <span class="highlight-text">até 5 MB</span>.</p>
          </div>
        </div>
      </div>
    </v-card-text>

    <v-card-actions class="card-actions">
      <v-row align="center" justify="space-between" class="actions-row">
        <div class="computation-info">
          <span class="bullet">&bull;</span>
          Última data de computação dos pontos:
          <strong>
            {{
              points.last_points_computation_at
                ? DateTime.fromISO(points.last_points_computation_at).isValid
                  ? DateTime.fromISO(points.last_points_computation_at).toFormat('dd/LL/yyyy HH:mm:ss')
                  : '-'
                : '-'
            }}
          </strong>
        </div>
        <div class="action-buttons">
          <mf-button class="logs-button" outlined label="Visualizar logs de alterações" @click="openLogsModal" />
          <mf-button :loading="saving" label="Atualizar programa de pontos" @click="updatePointsConfiguration" />
        </div>
      </v-row>
    </v-card-actions>

    <logs-modal v-model="logsDialog" tags="POINTS_CONFIGURATION" log-title="programa de pontos" :id-item="configurationId" />
  </v-card>
</template>
<script>
import gql from 'graphql-tag'
import { DateTime } from 'luxon'
import DatePicker from '@/components/DatePicker.vue'
import { MUTATION_SINGLE_UPLOAD_BASE64 } from '@/graphql'
import LogsModal from '@/components/logs-modal/LogsModal.vue'
import { MUTATION_POINTS } from '@/modules/configuracoes/gerais/graphql'

export default {
  components: { DatePicker, LogsModal },
  data: () => ({
    saving: false,
    logsDialog: false,
    configurationId: '',
    fixedDateTouched: false,
    points: {
      visible: false,
      homeButton: false,
      rate: 1,
      ruleUrl: '',
      durationType: 'DYNAMIC_DATE',
      duration: {
        scale: 'DAY',
        amount: 1,
        fixed_date: null,
      },
      start_date: '',
      end_date: '',
      last_points_computation_at: '',
    },
    pdfFile: {
      isLoading: false,
      fileContent: null,
      fileName: '',
    },
    errorPdf: '',
    DateTime,
  }),
  computed: {
    tabs() {
      return [
        { label: 'Data variável', value: 'DYNAMIC_DATE', active: true },
        { label: 'Data fixa', value: 'FIXED_DATE', active: true },
      ]
    },
    formattedFixedDate() {
      return this.points.duration.fixed_date ? this.DateTime.fromISO(this.points.duration.fixed_date).toFormat('dd/LL/yyyy') : null
    },
    hasPdf() {
      return this.pdfFile.fileContent !== null || !!this.points.ruleUrl
    },
    pdfFileName() {
      if (this.pdfFile.fileName) {
        return this.truncateFileName(this.pdfFile.fileName)
      }

      if (this.points.ruleUrl) {
        try {
          const url = new URL(this.points.ruleUrl)
          const pathname = url.pathname
          const fullFileName = decodeURIComponent(pathname.substring(pathname.lastIndexOf('/') + 1))
          const cleanName = fullFileName.substring(37)
          return this.truncateFileName(cleanName)
        } catch {
          return 'Documento.pdf'
        }
      }

      return 'Documento.pdf'
    },
    endDateError() {
      if (!this.points.end_date) return ''
      if (!this.points.start_date) return ''
      const start = this.DateTime.fromISO(this.points.start_date).startOf('day')
      const end = this.DateTime.fromISO(this.points.end_date).startOf('day')
      return end < start ? 'A data final não pode ser menor que a data inicial.' : ''
    },
    fixedDateError() {
      if (!this.points.duration.fixed_date) return ''
      if (!this.fixedDateTouched) return ''
      const today = this.DateTime.now().startOf('day')
      const selected = this.DateTime.fromISO(this.points.duration.fixed_date).startOf('day')
      return selected < today ? 'A data de expiração não pode ser menor que hoje.' : ''
    },
    dynamicAmountError() {
      if (this.points.durationType !== 'DYNAMIC_DATE') return ''
      const amount = Number(this.points.duration.amount)
      if (!amount || amount < 1) {
        return 'Informe um número maior ou igual a 1.'
      }
      return ''
    },
    rateError() {
      const rate = parseFloat(this.points.rate)
      if (this.points.rate === '' || this.points.rate === null || isNaN(rate)) {
        return 'Informe uma taxa de pontuação válida.'
      }
      if (rate <= 0) {
        return 'A taxa deve ser maior que zero.'
      }
      return ''
    },
    startDateError() {
      if (!this.points.start_date) {
        return 'A data inicial é obrigatória.'
      }
      return ''
    },
  },
  apollo: {
    buttons: {
      query: gql`
        query AppHomeButtons {
          appHomeButtons {
            id
            link
            type
          }
        }
      `,
      fetchPolicy: 'no-cache',
      update({ appHomeButtons }) {
        this.points.homeButton = appHomeButtons?.some((b) => b.type === 'INTERNAL' && b.link?.toLowerCase() === 'points') || false
      },
    },
    configuration: {
      query: gql`
        query Configuration {
          configuration {
            _id
            points {
              rate
              start_date
              end_date
              rule_url
              duration_type
              last_points_computation_at
              duration {
                scale
                amount
                fixed_date
              }
            }
          }
        }
      `,
      fetchPolicy: 'no-cache',
      update({ configuration }) {
        const config = configuration?.points || {}
        this.configurationId = configuration?._id || ''
        this.points = {
          ...this.points,
          rate: config.rate || 0,
          ruleUrl: config.rule_url || '',
          durationType: config.duration_type || 'FIXED_DATE',
          start_date: config.start_date || '',
          end_date: config.end_date || '',
          last_points_computation_at: config.last_points_computation_at ? config.last_points_computation_at.replace(' ', 'T') : '',
          duration: {
            scale: config.duration?.scale || 'DAY',
            amount: config.duration?.amount || 1,
            fixed_date: config.duration?.fixed_date || null,
          },
        }
      },
    },
  },
  watch: {
    'points.rate'(val) {
      if (typeof val === 'string' && val.startsWith('0') && !val.startsWith('0.')) {
        this.points.rate = val.replace(/^0+/, '') || '0'
      }
    },
  },
  methods: {
    async updatePointsConfiguration() {
      const confirmed = await this.$confirm({
        title: 'Confirmar atualização do programa de pontos',
        message:
          'Essa ação pode impactar regras sensíveis do seu programa de pontos, como vigência e expiração dos pontos. Tem certeza de que deseja continuar?',
        confirmText: 'Salvar',
        cancelText: 'Cancelar',
      })

      if (confirmed === false) return

      const hasErrors = this.endDateError || this.fixedDateError || this.dynamicAmountError || this.rateError || this.startDateError

      if (hasErrors) {
        this.$snackbar({
          message: 'Corrija os campos com erro antes de salvar.',
          snackbarColor: 'warning',
        })
        return
      }

      try {
        this.saving = true

        await this.$apollo.mutate({
          mutation: MUTATION_POINTS,
          variables: {
            homeButton: this.points.homeButton,
            durationType: this.points.durationType,
            rate: parseFloat(this.points.rate) || 0,
            startDate: this.points.start_date || null,
            endDate: this.points.end_date || null,
            ruleUrl: this.points.ruleUrl || null,
            duration: {
              scale: 'DAY',
              amount: Number(this.points.duration.amount),
              fixed_date: this.points.duration.fixed_date || null,
            },
            id: this.configurationId,
          },
        })

        this.$snackbar({ message: 'Programa de pontos atualizado com sucesso.', snackbarColor: 'success' })
      } catch {
        this.$snackbar({ message: 'Falha ao comunicar com o servidor.', snackbarColor: 'error' })
      } finally {
        this.saving = false
      }
    },
    triggerPdfInput() {
      this.$refs.pdfInput.value = null
      this.$refs.pdfInput.click()
    },
    truncateFileName(name) {
      return name.length > 16 ? name.substring(0, 16) + '...' : name
    },
    async readPdf(event) {
      const file = event.target.files[0]
      this.errorPdf = ''
      if (!file) return

      if (file.type !== 'application/pdf') {
        this.errorPdf = 'O arquivo precisa estar no formato PDF.'
        return
      }

      if (file.size > 5 * 1024 * 1024) {
        this.errorPdf = 'O tamanho máximo permitido é de 5 MB.'
        this.$snackbar({
          message: 'O tamanho máximo permitido é de 5 MB.',
          snackbarColor: 'error',
        })
        return
      }

      this.pdfFile.isLoading = true

      try {
        const reader = new FileReader()
        reader.onload = async () => {
          try {
            const base64 = reader.result
            const {
              data: { singleUploadBase64 },
            } = await this.$apollo.mutate({
              mutation: MUTATION_SINGLE_UPLOAD_BASE64,
              variables: {
                filename: file.name,
                base64,
              },
            })
            const { url } = singleUploadBase64
            this.points.ruleUrl = url
            this.pdfFile.fileContent = null
            this.pdfFile.fileName = file.name
          } catch (error) {
            this.$snackbar({ message: 'Erro ao fazer upload do PDF.', snackbarColor: 'error' })
          } finally {
            this.pdfFile.isLoading = false
          }
        }
        reader.readAsDataURL(file)
      } catch (error) {
        this.$snackbar({ message: 'Erro ao processar o arquivo PDF.', snackbarColor: 'error' })
        this.pdfFile.isLoading = false
      }
    },
    deletePdf() {
      this.pdfFile = {
        isLoading: false,
        fileContent: null,
        fileName: '',
      }
      this.points.ruleUrl = ''
    },
    openLogsModal() {
      this.logsDialog = true
    },
  },
}
</script>

<style scoped>
.points-config-card {
  margin-top: 12px;
  margin-bottom: 12px;
  padding-left: 24px;
  padding-right: 24px;
}

.points-title {
  font-size: 20px;
  font-weight: 700;
  color: #4b525a;
}

.divider {
  border: none;
  border-top: 1px solid #87919b;
  margin: 12px 0px;
}

.main-content {
  display: flex;
  gap: 36px;
  width: 100%;
  margin-top: 24px;
}

.definitions-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
}

.date-fields {
  display: flex;
  gap: 24px;
  margin-top: 32px;
  margin-bottom: 16px;
}

.date-field {
  flex: 1;
  width: 120px;
}

.date-separator {
  height: 68px;
  border-left: 1px solid #a5acb4;
}

.rate-field {
  margin-top: auto;
}

.rate-input {
  margin-top: 8px;
}

.expiration-section {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.expiration-header {
  margin-bottom: 16px;
}

.expiration-content {
  margin-top: auto;
}

.dynamic-description {
  margin-bottom: 12px;
}

.dynamic-input-container {
  display: flex;
  gap: 12px;
  align-items: center;
}

.amount-input {
  width: 140px;
}

.dynamic-result {
  margin-bottom: 28px;
}

.fixed-date-container {
  margin-bottom: 8px;
}

.fixed-description {
  margin-bottom: 12px;
}

.fixed-date-picker {
  width: 260px;
}

.pdf-section {
  flex: 1;
}

.hidden-input {
  display: none;
}

.file-name {
  font-size: 16px;
  font-weight: 400;
  color: #393939;
}

.close-icon {
  cursor: pointer;
  color: #4c4c4c;
}

.pdf-hint {
  font-size: 14px;
  font-weight: 400;
  color: #6b7580;
  margin-top: 16px;
}

.pdf-hint:last-of-type {
  margin-top: 8px;
}

.highlight-text {
  color: #00b37e;
  font-weight: 600;
}

.card-actions {
  padding-bottom: 12px;
  padding-top: 0px;
  padding-right: 12px;
}

.actions-row {
  width: 100%;
  margin-left: 8px;
}

.computation-info {
  font-size: 14px;
  color: #0057ff;
  display: flex;
  align-items: center;
  gap: 6px;
}

.bullet {
  font-size: 20px;
}

.action-buttons {
  display: flex;
  gap: 16px;
}

.logs-button {
  margin-right: 16px;
}

.title {
  font-size: 26px;
  font-weight: 700;
  color: #4b525a;
  font-family: 'Lato', sans-serif !important;
  display: block;
  margin-bottom: 16px;
}

.definitions-subtitle {
  font-size: 16px;
  font-weight: 700;
  color: #393939;
  font-family: 'Lato', sans-serif !important;
}

.expirations-subtitle {
  font-family: 'Lato', sans-serif !important;
  font-size: 14px;
  font-weight: 400;
  color: #6b7580;
}

.file-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  margin-left: 8px;
}

.error--text {
  font-size: 12px;
}

::v-deep input[type='number']::-webkit-inner-spin-button,
::v-deep input[type='number']::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

::v-deep input[type='number'] {
  -moz-appearance: textfield;
  appearance: textfield;
}

::v-deep .v-messages__message {
  color: #e03210 !important;
  font-size: 12px !important;
  font-weight: 400 !important;
  font-family: 'Lato', sans-serif !important;
}

::v-deep .v-input__slot {
  height: 45px !important;
  min-height: 45px !important;
}
</style>
